import React, { FC, useCallback, useEffect, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { KSpan, KTitleSpan, KInput, KDropdown, KButton, KTooltip, KSelectRange } from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  Legend
} from "chart.js"
import ChartDataLabels from "chartjs-plugin-datalabels"
import { Bar, Chart } from "react-chartjs-2"
import { toast } from "react-toastify"
import { handleAuthorizedRequest, users } from "../../../../../apis"
import { chartLabels, errorFilledToastOptions, successFilledToastOptions } from "../../../../../constants"
import { mapToCamelCase, addCommasToNumber, formatNumber, dateToYYYYMM } from "../../../../../utility"
import {
  AdvanceDealRow,
  AdvanceDealTrend,
  AdvancePortfolioKpi,
  AdvancePortfolioRiskConcentration,
  AdvancePortfolioTable,
  DistributionPortfolioTable,
  AdvancePortfolioSummaryKpi,
  DistributionDealRow,
  DistributionSummaryMetrics
} from "../../../../../types"
import Loading from "../../../../ui/Loading"
import Pagination from "../../../../ui/table/Pagination"
import SearchUserComponent from "../../../../ui/components/SearchUserComponent"
import { DateRangeType } from "kahuna-base-react-components/dist/components/KSelectRange/KSelectRange"
import AdvanceDealDetailPage from "./AdvanceDealDetailPage"
import { STATUS_STYLE, STATUS_TEXT } from "./dealStatus"
import { downloadPortfolioExport, PortfolioExportPayload } from "./downloadPortfolioExport"
import PlatformCompletenessInfo, { latestReportingMonths } from "../../../../ui/components/PlatformCompletenessInfo"

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  Legend
)

const GREEN = "#22c55e"
const RED = "#ef4444"
const TEXT_MUTED = "#6b7280"
const TEXT_PRIMARY = "#111"
const CHART_FONT = "Inter var, Inter, system-ui, -apple-system, sans-serif"

const RoyaltyDateOptionList: KSelectOption[] = [
  { label: "Activity Period", value: 1, value2: "activity_period" },
  { label: "Reporting Period", value: 2, value2: "reporting_period" }
]

type AdminPortfolioOverviewProps = {
  setOpenSidebar: (openSidebar: boolean) => void
  openSidebar: boolean
}

// ---- formatting -------------------------------------------------------------

const usd0 = (value: number | null | undefined) =>
  value === null || value === undefined || isNaN(value) ? "N/A" : `$${addCommasToNumber(Math.round(value))}`

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? "" : "s"}`

const compactUsd1 = (value: number) => {
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (Math.abs(value) >= 1_000) return `$${(value / 1_000).toFixed(1)}K`
  return `$${Math.round(value)}`
}

const usd2Signed = (value: number) =>
  `${value < 0 ? "-" : ""}$${Math.abs(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`

const compactUsdSigned = (value: number) => (value < 0 ? `-${compactUsd(-value)}` : compactUsd(value))

const compactUsd = (value: number) => {
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (Math.abs(value) >= 1_000) return `$${Math.round(value / 1_000)}K`
  return `$${Math.round(value)}`
}

const pct = (value: number | null | undefined, digits = 1) =>
  value === null || value === undefined || isNaN(value) ? "N/A" : `${parseFloat(value.toFixed(digits))}%`

const multipleX = (value: number | null | undefined) =>
  value === null || value === undefined || isNaN(value) ? "N/A" : `${value.toFixed(2)}x`

const xirrPct = (fraction: number | null | undefined) =>
  fraction === null || fraction === undefined || isNaN(fraction) ? "N/A" : `${(fraction * 100).toFixed(0)}%`

// Pace is signed: positive months mean the book is expected to recoup ahead of its target.
const paceText = (months: number | null | undefined) => {
  if (months === null || months === undefined || isNaN(months)) return "N/A"
  const rounded = parseFloat(months.toFixed(1))
  if (rounded === 0) return "On plan"
  return `${Math.abs(rounded)} mo ${rounded > 0 ? "ahead" : "behind"}`
}

const monthYear = (key: string | null | undefined) => {
  if (!key) return "N/A"
  // backend deal_date / contract_date are "YYYYMM"
  const year = key.slice(0, 4)
  const month = parseInt(key.slice(4, 6), 10)
  if (!year || isNaN(month)) return "N/A"
  const d = new Date(parseInt(year, 10), month - 1, 1)
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" })
}

// "YYYYMM" -> "July 2026"
const monthLongYear = (key: string | null | undefined) => {
  if (!key) return "N/A"
  const month = parseInt(key.slice(4, 6), 10)
  if (isNaN(month)) return "N/A"
  return new Date(parseInt(key.slice(0, 4), 10), month - 1, 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric"
  })
}

// "YYYYMM" -> "Dec. 2026"
const monthAbbrYear = (key: string | null | undefined) => {
  if (!key) return "N/A"
  const label = monthYear(key)
  return label === "N/A" ? label : label.replace(/^(\w{3})\b(?!\.)/, (m) => (m === "May" ? m : `${m}.`))
}

const toLocaleDate = (date: Date) => {
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" })
}

// ---- small components -------------------------------------------------------

const KpiCard: FC<{
  label: string
  value: string
  valueNote?: { amount: string; text: string }
  subtext?: string
  subRows?: { label: string; value: string }[]
  subtextColor?: string
}> = ({ label, value, valueNote, subtext, subRows, subtextColor = TEXT_MUTED }) => (
  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col gap-1.5">
    <KSpan text={label} fontSize={11} fontWeight={600} color={TEXT_MUTED} letterSpacing="0.05em" />
    <div className="flex items-baseline gap-1.5">
      <KTitleSpan text={value} fontSize={24} fontWeight={700} color={TEXT_PRIMARY} />
      {valueNote ? (
        <span>
          <KSpan text="(" fontSize={11} color={TEXT_MUTED} />
          <KSpan text={valueNote.amount} fontSize={11} fontWeight={700} color={TEXT_MUTED} />
          <KSpan text={` ${valueNote.text})`} fontSize={11} color={TEXT_MUTED} />
        </span>
      ) : null}
    </div>
    {subtext ? <KSpan text={subtext} fontSize={12} color={subtextColor} /> : null}
    {subRows?.map((row) => (
      <div key={row.label} className="flex justify-between">
        <KSpan text={row.label} fontSize={12} color={subtextColor} />
        <KSpan text={row.value} fontSize={12} fontWeight={700} color={subtextColor} />
      </div>
    ))}
  </div>
)

const BlurredKpiCard: FC<{
  label: string
  isLoading: boolean
  onClick: () => void
}> = ({ label, isLoading, onClick }) => (
  <div
    className={`bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col gap-1.5 ${
      isLoading ? "" : "cursor-pointer hover:border-black transition-colors"
    }`}
    onClick={isLoading ? undefined : onClick}
  >
    <KSpan text={label} fontSize={11} fontWeight={600} color={TEXT_MUTED} letterSpacing="0.05em" />
    {isLoading ? (
      <div className="flex items-center gap-2 h-[33px]">
        <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" />
        <KSpan text="Calculating..." fontSize={12} color={TEXT_MUTED} />
      </div>
    ) : (
      <div className="relative">
        <KTitleSpan text="---.--" fontSize={24} fontWeight={700} color={TEXT_PRIMARY} />
        <div className="absolute inset-0 backdrop-blur-sm bg-white/50 flex items-center rounded">
          <KSpan text="Click to calculate" fontSize={12} color={TEXT_MUTED} fontWeight={500} />
        </div>
      </div>
    )}
  </div>
)

// Gray summary card: centered caps label, then a left-aligned value and muted detail line.
const SummaryCard: FC<{ label: string; value: string; detail: string }> = ({ label, value, detail }) => (
  <div className="flex-1 p-3.5 border border-gray-100 rounded-xl bg-gray-50 text-center">
    <KSpan text={label} fontSize={11} fontWeight={600} color={TEXT_MUTED} letterSpacing="0.05em" />
    <div className="mt-1 text-left">
      <KTitleSpan text={value} fontSize={20} fontWeight={700} color={TEXT_PRIMARY} />
    </div>
    <div className="mt-0.5 text-left">
      <KSpan text={detail} fontSize={12} color={TEXT_MUTED} />
    </div>
  </div>
)

// Under the Portfolio Status chart: how many advances / artists crossed into or out of Timing Review.
const TimingReviewMovementCard: FC<{
  label: string
  movement: { artistCount: number; dealCount: number; outstanding: number; advance: number } | undefined
}> = ({ label, movement }) => (
  <SummaryCard
    label={label}
    value={`${plural(movement?.dealCount ?? 0, "advance")} / ${plural(movement?.artistCount ?? 0, "artist")}`}
    detail={`Outstanding ${compactUsd1(movement?.outstanding ?? 0)} · Advance ${compactUsd1(movement?.advance ?? 0)}`}
  />
)

// Summary card with two equal columns (value over a small muted label) split by a hairline,
// as in the design example's AVG RECOUP TIME card.
const SplitSummaryCard: FC<{ label: string; columns: { value: string; caption: string }[] }> = ({ label, columns }) => (
  <div className="flex-1 p-3.5 border border-gray-100 rounded-xl bg-gray-50 text-center">
    <KSpan text={label} fontSize={11} fontWeight={600} color={TEXT_MUTED} letterSpacing="0.05em" />
    <div className="flex mt-1">
      {columns.map((column, index) => (
        <div
          key={column.caption}
          className={`flex-1 min-w-0 text-left ${index > 0 ? "ml-2.5 pl-2.5 border-l border-gray-100" : ""}`}
        >
          <div>
            <KTitleSpan text={column.value} fontSize={20} fontWeight={700} color={TEXT_PRIMARY} />
          </div>
          <div className="mt-0.5">
            <KSpan text={column.caption} fontSize={12} color={TEXT_MUTED} />
          </div>
        </div>
      ))}
    </div>
  </div>
)

const trendGlyph = (trend: AdvanceDealTrend) => {
  if (trend === "up") return { char: "↗", color: GREEN }
  if (trend === "down") return { char: "↘", color: RED }
  return { char: "→", color: TEXT_MUTED }
}

const TrendCell: FC<{ trend: AdvanceDealTrend }> = ({ trend }) => {
  const g = trendGlyph(trend)
  return <span style={{ color: g.color, fontSize: 16, fontWeight: 700 }}>{g.char}</span>
}

const StatusBadge: FC<{ status: string }> = ({ status }) => {
  const s = STATUS_STYLE[status] || { label: status, bg: "#f1f5f9", color: TEXT_MUTED }
  return (
    <span
      style={{
        backgroundColor: s.bg,
        color: s.color,
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: "nowrap"
      }}
    >
      {s.label}
    </span>
  )
}

const ArtistCell: FC<{ name: string; image: string | null; onClick?: () => void }> = ({ name, image, onClick }) => (
  <div
    style={{ display: "flex", alignItems: "center", gap: 10, cursor: onClick ? "pointer" : "default" }}
    onClick={onClick}
  >
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        background: image ? `center / cover no-repeat url(${image})` : "#e2e8f0",
        flexShrink: 0
      }}
    />
    <KSpan text={name} fontSize={13} fontWeight={500} color={TEXT_PRIMARY} ellipsis />
  </div>
)

const PAGE_SIZE = 10

const ExcludeToggleCell: FC<{
  userId: number
  excluded: boolean
  isAdvanceDeal: boolean
  onToggle: (userId: number, excluded: boolean, isAdvanceDeal: boolean) => void
}> = ({ userId, excluded, isAdvanceDeal, onToggle }) => (
  <input
    type="checkbox"
    checked={excluded}
    title="Exclude from portfolio"
    onChange={() => onToggle(userId, excluded, isAdvanceDeal)}
    style={{ accentColor: "#111", cursor: "pointer" }}
  />
)

// Which of the last three reporting periods have landed for this artist, visible from the list
// without opening the deal. A single icon covers all three and fetches them in one call when
// clicked. The window is the reporting calendar's last three months rather than the row's own
// royalty months: a deal whose reports stopped in April is exactly the one that has to show three
// empty months, not three older months that happen to be full.
const ReportingStatusIcon: FC<{ row: AdvanceDealRow }> = ({ row }) => (
  <PlatformCompletenessInfo userId={row.userId} months={latestReportingMonths()} reportingPeriod />
)

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "10px 12px",
  fontSize: 11,
  fontWeight: 600,
  color: TEXT_MUTED,
  letterSpacing: "0.03em",
  borderBottom: "1px solid #e5e7eb",
  whiteSpace: "nowrap"
}

const tdStyle: React.CSSProperties = {
  padding: "12px",
  fontSize: 13,
  fontFamily: "Inter",
  color: TEXT_PRIMARY,
  borderBottom: "1px solid #f1f5f9",
  whiteSpace: "nowrap"
}

// Pins the first column of a table while the rest scrolls horizontally. The opaque background
// keeps scrolled cells from showing through.
// Faded red behind Timing Review rows. Opaque (the Timing Review bar color at ~18% over white)
// so the sticky Artist cell can wear it without scrolled cells showing through.
const TIMING_REVIEW_ROW_BG = "#FCF4F4"

const stickyFirstColumn: React.CSSProperties = {
  position: "sticky",
  left: 0,
  zIndex: 1,
  background: "#fff"
}

// ---- charts -----------------------------------------------------------------

// Shared by the Monthly Cash Flow and Portfolio Status charts: a dark HTML tooltip with the
// labels on the left, the values on the right and no color markers.
const htmlChartTooltip = (
  build: (dataIndex: number) => { title: string; primary?: string; rows: [string, string][] } | null
) => ({
  enabled: false,
  external: ({ chart, tooltip }: any) => {
    const parent = chart.canvas.parentNode as HTMLElement
    let el = parent.querySelector<HTMLElement>(".chart-html-tooltip")
    if (!el) {
      el = document.createElement("div")
      el.className = "chart-html-tooltip"
      Object.assign(el.style, {
        position: "absolute",
        pointerEvents: "none",
        padding: "6px 8px",
        borderRadius: "6px",
        background: "rgba(0, 0, 0, 0.8)",
        color: "#fff",
        fontFamily: CHART_FONT,
        fontSize: "12px",
        lineHeight: "16px",
        whiteSpace: "nowrap",
        zIndex: "10",
        transition: "opacity 0.1s"
      })
      parent.appendChild(el)
    }
    if (tooltip.opacity === 0 || !tooltip.dataPoints?.length) {
      el.style.opacity = "0"
      return
    }
    const content = build(tooltip.dataPoints[0].dataIndex)
    if (!content) {
      el.style.opacity = "0"
      return
    }
    const { title, primary, rows } = content
    const rowsHtml = rows
      .map(
        ([label, value]) =>
          `<div style="display:flex;justify-content:space-between;gap:16px"><span>${label}</span><span>${value}</span></div>`
      )
      .join("")
    const primaryHtml = primary ? `<div style="font-weight:700;margin:2px 0 6px">${primary}</div>` : ""
    el.innerHTML = `<div style="font-weight:700;margin-bottom:2px">${title}</div>${primaryHtml}${rowsHtml}`
    el.style.opacity = "1"
    const left = Math.max(0, Math.min(chart.width - el.offsetWidth, tooltip.caretX - el.offsetWidth / 2))
    const top = Math.max(0, tooltip.caretY - el.offsetHeight - 10)
    el.style.left = `${left}px`
    el.style.top = `${top}px`
  }
})

// Portfolio Status: outstanding balance per status, with artist / advance counts above each bar
// and the share of the portfolio's outstanding inside it. Palette from the design example.
// The Portfolio Status and Monthly Vintage Recovery plots sit side by side and must have the same
// plot (y-axis) height. Both charts reserve the same top space (a legend row plus headroom for the
// labels above the bars) and the same fixed-height x axis. The Vintage legend is plain HTML laid
// over that top space, not a Chart.js legend, so its size cannot change the plot.
const CHART_CARD_HEADER_HEIGHT = 64
const CHART_CARD_PLOT_HEIGHT = 320
const CHART_LEGEND_ROW_HEIGHT = 24
const CHART_LABEL_HEADROOM = 40
const CHART_X_AXIS_HEIGHT = 52
const fixXAxisHeight = (scale: any) => {
  scale.height = CHART_X_AXIS_HEIGHT
}

const VintageLegend: FC = () => (
  <div
    className="absolute top-0 right-0 flex items-center gap-4"
    style={{ height: CHART_LEGEND_ROW_HEIGHT, pointerEvents: "none" }}
  >
    {[
      { label: "Company Recouped", color: VINTAGE_RECOUPED },
      { label: "Deployed", color: VINTAGE_DEPLOYED }
    ].map((item) => (
      <div key={item.label} className="flex items-center gap-1.5">
        <span
          style={{ width: 12, height: 10, borderRadius: 2, backgroundColor: item.color, display: "inline-block" }}
        />
        <KSpan text={item.label} fontSize={CASH_FLOW_FONT_SIZE} color={CASH_FLOW_TICK} lineHeight="16px" />
      </div>
    ))}
  </div>
)

const PORTFOLIO_STATUS_ORDER = ["ahead", "on_track", "timing_review", "new"]
const PORTFOLIO_STATUS_META: Record<string, { label: string; bar: string }> = {
  ahead: { label: "Ahead", bar: "#AAB9D5" },
  on_track: { label: "On Track", bar: "#9BBFAD" },
  timing_review: { label: "Timing Review", bar: "#D1ADAD" },
  new: { label: "New", bar: "#D8E8D5" }
}
const MIN_BAR_HEIGHT_FOR_INSIDE_LABEL = 26

const PortfolioStatusChart: FC<{
  portfolio: AdvancePortfolioKpi
  activeStatuses: string[]
  onSelectStatus: (status: string) => void
}> = ({ portfolio, activeStatuses, onSelectStatus }) => {
  const buckets = PORTFOLIO_STATUS_ORDER.map(
    (status) =>
      portfolio.portfolioStatusChart?.buckets?.find((b) => b.status === status) ?? {
        status,
        artistCount: 0,
        dealCount: 0,
        outstanding: 0
      }
  )
  const totalOutstanding = buckets.reduce((sum, b) => sum + b.outstanding, 0)
  // Denominators are the chart's own totals, so each label reads as a share of what is plotted.
  const artistsDenominator = buckets.reduce((sum, b) => sum + b.artistCount, 0)
  const advancesDenominator = buckets.reduce((sum, b) => sum + b.dealCount, 0)
  const share = (value: number) => (totalOutstanding ? `${((value / totalOutstanding) * 100).toFixed(1)}%` : "0.0%")

  const data = {
    labels: buckets.map((b) => PORTFOLIO_STATUS_META[b.status].label),
    datasets: [
      {
        data: buckets.map((b) => b.outstanding),
        // With a status filter active, the bars outside it are faded.
        backgroundColor: buckets.map((b) =>
          activeStatuses.length === 0 || activeStatuses.includes(b.status)
            ? PORTFOLIO_STATUS_META[b.status].bar
            : `${PORTFOLIO_STATUS_META[b.status].bar}55`
        ),
        borderRadius: { topLeft: 6, topRight: 6 },
        borderSkipped: "bottom" as const,
        maxBarThickness: 56
      }
    ]
  }
  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    // Headroom for the two-line count label above the tallest bar, plus the legend row the
    // vintage chart draws, so both plots start at the same height.
    layout: { padding: { top: CHART_LEGEND_ROW_HEIGHT + CHART_LABEL_HEADROOM } },
    // Unified hover: anywhere in a bar's vertical column (not only on the bar itself) counts.
    interaction: { mode: "index", intersect: false },
    // Clicking a bar filters the advance deals table to that status.
    onClick: (_event: any, elements: any[]) => {
      if (elements.length) onSelectStatus(buckets[elements[0].index].status)
    },
    onHover: (event: any, elements: any[]) => {
      if (event.native?.target) event.native.target.style.cursor = elements.length ? "pointer" : "default"
    },
    plugins: {
      legend: { display: false },
      tooltip: htmlChartTooltip((index) => {
        const b = buckets[index]
        return {
          title: PORTFOLIO_STATUS_META[b.status].label,
          rows: [
            ["Artists", `${b.artistCount} / ${artistsDenominator}`],
            ["Advances", `${b.dealCount} / ${advancesDenominator}`],
            ["Company Outstanding Balance", usd0(b.outstanding)],
            ["Share of Outstanding", share(b.outstanding)]
          ]
        }
      }),
      datalabels: {
        labels: {
          counts: {
            anchor: "end",
            align: "end",
            offset: 2,
            color: TEXT_PRIMARY,
            textAlign: "center",
            font: { size: CASH_FLOW_FONT_SIZE, weight: 600, family: CHART_FONT },
            formatter: (_v: number, ctx: any) => {
              const b = buckets[ctx.dataIndex]
              return [
                `${b.artistCount}/${artistsDenominator} artists`,
                `${b.dealCount}/${advancesDenominator} advances`
              ]
            }
          },
          share: {
            anchor: "center",
            align: "center",
            color: STATUS_TEXT,
            font: { size: CASH_FLOW_FONT_SIZE, weight: 700, family: CHART_FONT },
            display: (ctx: any) => {
              const bar = ctx.chart.getDatasetMeta(ctx.datasetIndex).data[ctx.dataIndex] as any
              return !!bar && bar.height >= MIN_BAR_HEIGHT_FOR_INSIDE_LABEL
            },
            formatter: (value: number) => share(value)
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: CASH_FLOW_TICK, font: { family: CHART_FONT, size: CASH_FLOW_FONT_SIZE } },
        afterFit: fixXAxisHeight
      },
      y: {
        beginAtZero: true,
        grace: "8%",
        ticks: {
          callback: (v: any) => compactUsd(Number(v)),
          color: CASH_FLOW_TICK,
          font: { family: CHART_FONT, size: CASH_FLOW_FONT_SIZE }
        },
        grid: { color: "#f1f5f9" },
        title: {
          display: true,
          text: "Company Outstanding Balance ($)",
          color: CASH_FLOW_TICK,
          font: { family: CHART_FONT, size: CASH_FLOW_FONT_SIZE }
        }
      }
    }
  }
  return <Bar data={data} options={options} plugins={[ChartDataLabels as any]} />
}

// Monthly Vintage Recovery: for each month's deployments (its vintage), the cash deployed next
// to how much of it the company has recouped. Months with no deployments stay as empty rows.
const VINTAGE_DEPLOYED = "#2F6B4F"
const VINTAGE_RECOUPED = "#22c55e"

const MonthlyVintageChart: FC<{ portfolio: AdvancePortfolioKpi }> = ({ portfolio }) => {
  const months = [...(portfolio.vintageRecoveryChart?.months ?? [])].sort((a, b) => a.month.localeCompare(b.month))
  const parts = months.map((m) => monthYear(m.month).split(" "))
  // Tick = "Mon", plus the year on the first month and wherever the year changes.
  const tickLabel = (i: number) => {
    const [mon, year] = parts[i]
    return i === 0 || year !== parts[i - 1][1] ? `${mon} '${year.slice(2)}` : mon
  }
  const pct = (m: { deployed: number; companyRecouped: number }) =>
    m.deployed ? (m.companyRecouped / m.deployed) * 100 : 0

  // One stacked bar per month, as tall as that month's deployments: the recouped part fills it
  // from the bottom like a progress bar and the rest stays dark.
  const recouped = months.map((m) => Math.min(Math.max(m.companyRecouped, 0), m.deployed))
  const remaining = months.map((m, i) => Math.max(m.deployed - recouped[i], 0))
  const topRadius = { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 }

  const data = {
    labels: months.map((_, i) => tickLabel(i)),
    datasets: [
      {
        label: "Company Recouped",
        data: recouped,
        backgroundColor: VINTAGE_RECOUPED,
        // Round the top only when nothing sits above it (a fully recouped month).
        borderRadius: (ctx: any) => (remaining[ctx.dataIndex] > 0 ? 0 : topRadius),
        borderSkipped: false,
        maxBarThickness: 22,
        datalabels: { display: false }
      },
      {
        label: "Deployed",
        data: remaining,
        backgroundColor: VINTAGE_DEPLOYED,
        borderRadius: topRadius,
        borderSkipped: false,
        maxBarThickness: 22,
        // Recouped % of that month's deployments, vertical above the bar; omitted when nothing
        // was deployed.
        datalabels: {
          rotation: -90,
          anchor: "end",
          align: "end",
          offset: 2,
          color: TEXT_MUTED,
          font: { size: CASH_FLOW_FONT_SIZE, weight: 500, family: CHART_FONT },
          display: (ctx: any) => months[ctx.dataIndex].deployed > 0,
          formatter: (_v: number, ctx: any) => `${Math.round(pct(months[ctx.dataIndex]))}%`
        }
      }
    ]
  }
  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    // The legend row (HTML, see below) plus headroom for the vertical % label above the tallest bar.
    layout: { padding: { top: CHART_LEGEND_ROW_HEIGHT + CHART_LABEL_HEADROOM } },
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: htmlChartTooltip((index) => {
        const m = months[index]
        if (!m.deployed) return null
        return {
          title: monthYear(m.month),
          primary: `${usd0(m.companyRecouped)} / ${usd0(m.deployed)} (${pct(m).toFixed(1)}%)`,
          rows: [
            ["Recouped Deals", `${m.recoupedDeals} / ${m.deals}`],
            ["Artists", String(m.artists)],
            ["Avg Advance", usd0(m.avgAdvance)],
            ["Median Advance", usd0(m.medAdvance)],
            ["Target Recoup", `${m.targetRecoup.toFixed(1)} mo WA`],
            ["Contract Term", `${m.contractTerm.toFixed(1)} mo WA`],
            ["Pre-Recoup Comm.", `${m.preRecoupCommission.toFixed(1)}% WA`],
            ["Post-Recoup Comm.", `${m.postRecoupCommission.toFixed(1)}% WA`]
          ]
        }
      })
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: {
          color: CASH_FLOW_TICK,
          font: { family: CHART_FONT, size: CASH_FLOW_FONT_SIZE },
          maxRotation: 45,
          minRotation: 45,
          autoSkip: false
        },
        afterFit: fixXAxisHeight
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grace: "8%",
        ticks: {
          callback: (v: any) => compactUsd(Number(v)),
          color: CASH_FLOW_TICK,
          font: { family: CHART_FONT, size: CASH_FLOW_FONT_SIZE }
        },
        grid: { color: "#f1f5f9" }
      }
    }
  }
  return <Bar data={data} options={options} plugins={[ChartDataLabels as any]} />
}

const CASH_FLOW_REVENUE = "#34C96B"
const CASH_FLOW_ADVANCE = "#1F6B4A"
const CASH_FLOW_NET = "#374151"
// The example's #4A82B7 at a soft opacity: a trend layer, lighter than the bars and dots.
const CASH_FLOW_CUMULATIVE = "rgba(74, 130, 183, 0.6)"
const CASH_FLOW_TICK = "#666"
const CASH_FLOW_FONT_SIZE = 12
const CASH_FLOW_MAX_MONTHS = 36
const CASH_FLOW_LEGEND_ORDER = ["Collected Revenue", "Advances Funded", "Net Cash", "Cumulative Net Cash"]
const CASH_FLOW_LEGEND_STYLE: Record<string, string> = {
  "Collected Revenue": "rectRounded",
  "Advances Funded": "rectRounded",
  "Cumulative Net Cash": "line"
}
// Chart.js sizes every legend marker from one shared box size, so the small Net Cash dot is a
// pre-drawn canvas instead.
const CASH_FLOW_LEGEND_DOT_SIZE = 6
const cashFlowLegendDot = () => {
  const canvas = document.createElement("canvas")
  canvas.width = CASH_FLOW_LEGEND_DOT_SIZE
  canvas.height = CASH_FLOW_LEGEND_DOT_SIZE
  const ctx = canvas.getContext("2d")
  if (ctx) {
    ctx.fillStyle = CASH_FLOW_NET
    ctx.beginPath()
    ctx.arc(CASH_FLOW_LEGEND_DOT_SIZE / 2, CASH_FLOW_LEGEND_DOT_SIZE / 2, CASH_FLOW_LEGEND_DOT_SIZE / 2, 0, Math.PI * 2)
    ctx.fill()
  }
  return canvas
}

const MonthlyCashFlowChart: FC<{ portfolio: AdvancePortfolioKpi }> = ({ portfolio }) => {
  const currentMonth = dateToYYYYMM(new Date())
  const months = [...portfolio.monthlyChart.months]
    .filter((m) => m.month <= currentMonth)
    .sort((a, b) => a.month.localeCompare(b.month))

  // The running total starts at the first available month, so trimming to the latest
  // CASH_FLOW_MAX_MONTHS below never resets it.
  let running = 0
  const allRows = months.map((m) => {
    const revenue = m.revenue ?? 0
    const advance = m.advances ?? 0
    const net = revenue - advance
    running += net
    return {
      month: m.month,
      revenue,
      recoupment: m.recoupment ?? 0,
      profit: m.profit ?? 0,
      advance,
      net,
      cumulative: running
    }
  })
  const rows = allRows.slice(-CASH_FLOW_MAX_MONTHS)

  const labels = rows.map((r) => {
    const [mon, year] = monthYear(r.month).split(" ")
    return `${mon} '${year.slice(2)}`
  })

  const data: any = {
    labels,
    datasets: [
      {
        type: "line",
        label: "Cumulative Net Cash",
        data: rows.map((r) => r.cumulative),
        borderColor: CASH_FLOW_CUMULATIVE,
        borderWidth: 2,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 0,
        order: 3
      },
      {
        type: "bar",
        label: "Collected Revenue",
        data: rows.map((r) => r.revenue),
        backgroundColor: CASH_FLOW_REVENUE,
        borderRadius: 2,
        maxBarThickness: 22,
        stack: "cashFlow",
        order: 2
      },
      {
        type: "bar",
        label: "Advances Funded",
        data: rows.map((r) => -r.advance),
        backgroundColor: CASH_FLOW_ADVANCE,
        borderRadius: 2,
        maxBarThickness: 22,
        stack: "cashFlow",
        order: 2
      },
      {
        type: "line",
        label: "Net Cash",
        data: rows.map((r) => r.net),
        showLine: false,
        backgroundColor: CASH_FLOW_NET,
        borderColor: "#fff",
        borderWidth: 1,
        pointRadius: 4.5,
        pointHoverRadius: 5.5,
        order: 1
      }
    ]
  }

  const legendDot = cashFlowLegendDot()
  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        position: "top",
        align: "end",
        labels: {
          usePointStyle: true,
          boxWidth: 12,
          boxHeight: 10,
          padding: 16,
          color: CASH_FLOW_TICK,
          font: { family: CHART_FONT, size: CASH_FLOW_FONT_SIZE },
          generateLabels: (chart: any) =>
            ChartJS.defaults.plugins.legend.labels
              .generateLabels(chart)
              .map((item: any) => {
                const color = chart.data.datasets[item.datasetIndex].backgroundColor
                const isLine = item.text === "Cumulative Net Cash"
                return {
                  ...item,
                  pointStyle: item.text === "Net Cash" ? legendDot : CASH_FLOW_LEGEND_STYLE[item.text] ?? "rect",
                  fillStyle: isLine ? "transparent" : color,
                  strokeStyle: isLine ? CASH_FLOW_CUMULATIVE : color,
                  lineWidth: isLine ? 2 : 0
                }
              })
              .sort((a: any, b: any) => CASH_FLOW_LEGEND_ORDER.indexOf(a.text) - CASH_FLOW_LEGEND_ORDER.indexOf(b.text))
        }
      },
      tooltip: htmlChartTooltip((index) => {
        const row = rows[index]
        return {
          title: monthYear(row.month),
          rows: [
            ["Collected Revenue", usd2Signed(row.revenue)],
            ["Recoupment", usd2Signed(row.recoupment)],
            ["Profit", usd2Signed(row.profit)],
            ["Advances Funded", usd2Signed(row.advance)],
            ["Net Cash", usd2Signed(row.net)],
            ["Cumulative Net Cash", usd2Signed(row.cumulative)]
          ]
        }
      }),
      datalabels: { display: false }
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: {
          color: CASH_FLOW_TICK,
          font: { family: CHART_FONT, size: CASH_FLOW_FONT_SIZE },
          maxRotation: 45,
          minRotation: 45
        }
      },
      // Everything shares this one axis so the cumulative line reads true against the bars.
      // Not value-stacked: the bars diverge from zero on their own and the lines stay exact.
      y: {
        grace: "10%",
        ticks: {
          callback: (v: any) => compactUsdSigned(Number(v)),
          color: CASH_FLOW_TICK,
          font: { family: CHART_FONT, size: CASH_FLOW_FONT_SIZE }
        },
        grid: { color: (ctx: any) => (ctx.tick?.value === 0 ? "#cbd5e1" : "#f1f5f9") }
      }
    }
  }
  return <Chart type="bar" data={data} options={options} />
}

const DealStatusOptions: KSelectOption[] = [
  { label: "Ahead", value: 1, value2: "ahead" },
  { label: "On Track", value: 2, value2: "on_track" },
  { label: "Timing Review", value: 3, value2: "timing_review" },
  { label: "New", value: 4, value2: "new" }
]

const RecoupedOptions: KSelectOption[] = [
  { label: "Not Recouped", value: 1, value2: "not" },
  { label: "Company Recouped", value: 2, value2: "company" },
  { label: "Artist Recouped", value: 3, value2: "artist" }
]

// The backend needs both bounds, so a missing max is sent as this very large number.
const ADVANCE_SIZE_OPEN_MAX = 1000000000

const RangeFilter: FC<{
  label: string
  minPlaceholder: string
  maxPlaceholder: string
  min: string
  max: string
  onChange: (min: string, max: string) => void
}> = ({ label, minPlaceholder, maxPlaceholder, min, max, onChange }) => {
  const inputClass = "h-9 w-[110px] rounded-md border border-gray-300 px-2 text-[13px] outline-none focus:border-black"
  return (
    <div className="flex gap-2 shrink-0 items-center">
      <KSpan text={`${label}:`} fontSize={12} fontWeight={500} color={TEXT_MUTED} />
      <input
        className={inputClass}
        type="number"
        min={0}
        placeholder={minPlaceholder}
        value={min}
        onChange={(e) => onChange(e.target.value, max)}
      />
      <KSpan text="–" fontSize={12} color={TEXT_MUTED} />
      <input
        className={inputClass}
        type="number"
        min={0}
        placeholder={maxPlaceholder}
        value={max}
        onChange={(e) => onChange(min, e.target.value)}
      />
    </div>
  )
}

// Same open-ended handling for the target recoup months range.
const TARGET_RECOUP_OPEN_MAX = 1000

const AccountTypeOptions: KSelectOption[] = [
  { label: "Advance", value: 1, value2: "advance" },
  { label: "Distribution", value: 2, value2: "distribution" }
]

const DistributionFeeOptions: KSelectOption[] = [
  { label: "< 10%", value: 1, value2: "0,10" },
  { label: "10–15%", value: 2, value2: "10,15" },
  { label: "15-20%", value: 3, value2: "15,20" },
  { label: "20-25%", value: 4, value2: "20,25" },
  { label: "25-30%", value: 5, value2: "25,30" },
  { label: "30-35%", value: 6, value2: "30,35" },
  { label: "> 35%", value: 7, value2: "35,100" }
]

const FilterDropdown: FC<{
  label: string
  selected: KSelectOption | KSelectOption[] | undefined
  options: KSelectOption[]
  isMulti: boolean
  width: number
  menuWidth: number
  onSelect: (option: KSelectOption | KSelectOption[] | undefined) => void
}> = ({ label, selected, options, isMulti, onSelect, width, menuWidth }) => {
  // A multi-select would grow taller with every chip and push the rest of the page around, so
  // it keeps the single-select height and shows its chips on one clipped line instead (the
  // selection is spelled out in the hover title).
  const multiLine = isMulti ? "[&_.k-dropdown>div:first-of-type>div:first-child]:!flex-nowrap" : ""
  const multiChips = isMulti ? "[&_.k-dropdown>div:first-of-type>div:first-child>div]:shrink-0" : ""
  const selectedLabels = Array.isArray(selected) ? selected.map((option) => option.label).join(", ") : undefined

  return (
    <div className="flex gap-2 shrink-0">
      <div className="mt-1 whitespace-nowrap">
        <KSpan text={`${label}:`} fontSize={12} fontWeight={500} color={TEXT_MUTED} />
      </div>
      <div className={`${multiLine} ${multiChips}`} title={selectedLabels || undefined}>
        <KDropdown
          height={36}
          padding={isMulti ? "1px 8px" : undefined}
          width={width}
          menuWidth={menuWidth}
          closeMenuOnSelect={isMulti ? false : true}
          isMulti={isMulti}
          selected={selected}
          isClearable
          options={options}
          onSelect={(s) => onSelect(s as KSelectOption)}
        />
      </div>
    </div>
  )
}

const monthsOneDecimal = (value: number | null | undefined) =>
  value === null || value === undefined || isNaN(value) ? "N/A" : `${value.toFixed(1)} mo`

const monthsText = (value: number | null | undefined) =>
  value === null || value === undefined ? "N/A" : `${Math.round(value)} mo`

// Company Recoup cell — estimated total months on top, realized below. A
// recouped deal shows its closing duration on the bottom row.
const CompanyRecoupCell: FC<{ row: AdvanceDealRow }> = ({ row }) => {
  if (row.recouped) {
    return (
      <>
        <KSpan text="Recouped" fontSize={13} fontWeight={500} color={GREEN} />
        <div>
          <KSpan text={`closed ${row.companyRecoupLag} mo`} fontSize={11} color={TEXT_MUTED} />
        </div>
      </>
    )
  }
  return (
    <>
      <KSpan text={`${row.companyRecoupLag} mo`} fontSize={13} />
    </>
  )
}

// Recoup Timing Delta = target − total recoup months (realized + estimation).
// Negative => recoup running past the deal target (shown red). For deals not
// yet recouped, the months still remaining are shown in parentheses.
const RecoupTimingDeltaCell: FC<{ row: AdvanceDealRow }> = ({ row }) => {
  const delta = row.recoupTimingDelta
  if (delta === null || delta === undefined) return <span style={{ color: TEXT_MUTED }}>N/A</span>
  const rounded = Math.round(delta)

  return (
    <>
      <KSpan text={`${rounded > 0 ? "+" : ""}${rounded} mo`} fontSize={13} color={rounded < 0 ? RED : GREEN} />
    </>
  )
}

// Portfolio Risk & Concentration: quiet reference data, grouped on light-gray surfaces.
const RISK_GROUP_BG = "#f3f4f6"
const RISK_GROUP_BORDER = "#e5e7eb"

const riskPct = (value: number | null | undefined) =>
  value === null || value === undefined || isNaN(value) ? "N/A" : `${value.toFixed(1)}%`

const RiskGroup: FC<{ title: string; rows: [string, number | null | undefined][]; valueHeader?: string }> = ({
  title,
  rows,
  valueHeader = "Current"
}) => (
  <div
    className="flex-1 min-w-[240px]"
    style={{
      background: RISK_GROUP_BG,
      border: `1px solid ${RISK_GROUP_BORDER}`,
      borderRadius: 10,
      padding: "12px 14px"
    }}
  >
    <div
      className="flex justify-between gap-3 uppercase"
      style={{ paddingBottom: 6, borderBottom: `1px solid ${RISK_GROUP_BORDER}` }}
    >
      <KSpan text={title} fontSize={12} fontWeight={600} color={TEXT_MUTED} />
      <KSpan text={valueHeader} fontSize={12} fontWeight={600} color={TEXT_MUTED} />
    </div>
    {rows.map(([label, value], index) => (
      <div
        key={label}
        className="flex justify-between items-baseline gap-3"
        style={{
          padding: "8px 0",
          borderBottom: index === rows.length - 1 ? "none" : `1px solid ${RISK_GROUP_BORDER}`
        }}
      >
        <KSpan text={label} fontSize={14} color="#3f3f46" />
        <KSpan text={riskPct(value)} fontSize={14} fontWeight={600} color={TEXT_PRIMARY} />
      </div>
    ))}
  </div>
)

const RiskConcentrationCard: FC<{ risk: AdvancePortfolioRiskConcentration }> = ({ risk }) => (
  <div className="bg-white rounded-xl border border-gray-200 min-w-0" style={{ padding: "16px 20px" }}>
    <div style={{ marginBottom: 14 }}>
      <KTitleSpan text="Portfolio Risk & Concentration" fontSize={20} fontWeight={700} lineHeight="28px" />
    </div>
    <div className="flex flex-wrap gap-4">
      <RiskGroup
        title="Exposure Concentration"
        rows={[
          ["Largest Artist Exposure", risk.largestArtistOutstanding],
          ["Top 5 Artist Exposure", risk.top5ArtistOutstanding],
          ["Single Eligible Receivable", risk.singleDealOutstanding],
          ["Largest 5 Eligible Receivables", risk.top5DealOutstanding]
        ]}
      />
      <RiskGroup
        title="Recovery / Tenor"
        rows={[
          ["Timing Review Exposure", risk.timingReviewExposure],
          ["Target Recoupment >12M", risk.targetRecoup12M],
          ["Target Recoupment ≥24M", risk.targetRecoup24M]
        ]}
      />
      <RiskGroup
        title="Platform Concentration"
        rows={[
          ["Largest DSP Share", risk.largestDspShare],
          ["Top 3 DSP Share", risk.top3DspShare]
        ]}
      />
    </div>
    <hr style={{ margin: "16px 0 14px", border: 0, borderTop: "1px solid #e5e7eb" }} />
    <div className="uppercase" style={{ marginBottom: 10, letterSpacing: "0.05em" }}>
      <KSpan text="Advance Revenue Mix" fontSize={12} fontWeight={600} color={TEXT_MUTED} />
    </div>
    <div className="flex flex-wrap gap-4">
      <RiskGroup
        title="Platform Mix"
        valueHeader="Gross Collections"
        rows={(risk.platformMix ?? []).map((item) => [
          (chartLabels.platform as Record<string, string>)[item.platform] ?? item.platform,
          item.share
        ])}
      />
      <RiskGroup
        title="Territory Mix"
        valueHeader="Gross Collections"
        rows={(risk.territoryMix ?? []).map((item) => [item.country, item.share])}
      />
      <RiskGroup
        title="Catalogue Mix"
        valueHeader="Company Recovery"
        rows={[
          ["New Release", risk.catalogueMixNewRelease],
          ["Back Catalog", risk.catalogueMixBackCatalog],
          ["Takedown", risk.catalogueMixTakedown]
        ]}
      />
    </div>
  </div>
)

// Progress bar + rounded percent used by the Company / Artist Rec. % columns.
const RecoupPctBar: FC<{ pct: number; complete: boolean }> = ({ pct, complete }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    <div style={{ flex: 1, height: 6, background: "#f1f5f9", borderRadius: 999, minWidth: 60 }}>
      <div
        style={{
          width: `${Math.min(100, pct)}%`,
          height: "100%",
          background: complete ? GREEN : TEXT_MUTED,
          borderRadius: 999
        }}
      />
    </div>
    <span style={{ fontSize: 12, color: TEXT_MUTED }}>{Math.round(pct)}%</span>
  </div>
)

const AdvanceDealsTable: FC<{
  rows: AdvanceDealRow[]
  latestCompleteMonth: string | null | undefined
  count: number
  page: number
  statusFilter: KSelectOption[]
  royaltyDateFilter: KSelectOption | undefined
  recoupedFilter: KSelectOption | undefined
  previousStatusFilter: KSelectOption[]
  advanceMin: string
  advanceMax: string
  targetMin: string
  targetMax: string
  contractEndSort: "asc" | "desc" | undefined
  latestAdvanceSort: "asc" | "desc" | undefined
  loading: boolean
  selectedUser: KSelectOption | undefined
  setSelectedUser: (selectedUser: KSelectOption | undefined) => void
  onRoyaltyDateChange: (option: KSelectOption | KSelectOption[] | undefined) => void
  onApplyFilter: () => void
  onStatusChange: (option: KSelectOption | KSelectOption[] | undefined) => void
  onRecoupedChange: (option: KSelectOption | KSelectOption[] | undefined) => void
  onPreviousStatusChange: (option: KSelectOption | KSelectOption[] | undefined) => void
  onAdvanceSizeChange: (min: string, max: string) => void
  onTargetRecoupChange: (min: string, max: string) => void
  onToggleContractEndSort: () => void
  onToggleLatestAdvanceSort: () => void
  onPageChange: (page: number) => void
  onToggleExclude: (userId: number, excluded: boolean, isAdvanceDeal: boolean) => void
  onSelectDeal: (selectedDeal: AdvanceDealRow) => void
}> = ({
  rows,
  latestCompleteMonth,
  count,
  page,
  royaltyDateFilter,
  statusFilter,
  recoupedFilter,
  previousStatusFilter,
  advanceMin,
  advanceMax,
  targetMin,
  targetMax,
  selectedUser,
  contractEndSort,
  latestAdvanceSort,
  loading,
  onStatusChange,
  onRoyaltyDateChange,
  onRecoupedChange,
  onPreviousStatusChange,
  onAdvanceSizeChange,
  onTargetRecoupChange,
  onApplyFilter,
  setSelectedUser,
  onToggleContractEndSort,
  onToggleLatestAdvanceSort,
  onPageChange,
  onToggleExclude,
  onSelectDeal
}) => {
  const pageCount = Math.max(1, Math.ceil(count / PAGE_SIZE))
  const dash = <span style={{ color: TEXT_MUTED }}>—</span>
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 min-w-0">
      {/* z-10 keeps the filter menus above the table's sticky Artist column */}
      <div className="relative z-10 mb-4 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <KTitleSpan text="Advance Deals" fontSize={20} fontWeight={700} lineHeight="28px" />
          {latestCompleteMonth && (
            <span
              className="group inline-flex items-center overflow-hidden rounded-full px-3 py-1 cursor-default transition-all duration-300 ease-in-out"
              style={{ background: "#F1EBFF", border: "1px solid #D6C8FA" }}
            >
              {/* Collapsed to zero width until hover, then grows to reveal what the date means. Plain
                  spans with a fixed line height: KSpan's default line height pushes the text off-centre. */}
              <span
                className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-300 ease-in-out group-hover:mr-1.5 group-hover:max-w-[160px] group-hover:opacity-100"
                style={{ fontSize: 12, lineHeight: "18px", color: "#6B46C1" }}
              >
                Latest complete month:
              </span>
              <span
                className="whitespace-nowrap"
                style={{ fontSize: 12, lineHeight: "18px", fontWeight: 600, color: "#5B34B0" }}
              >
                {monthLongYear(latestCompleteMonth)}
              </span>
            </span>
          )}
        </div>
        {/* First line spans the full card width (controls spread edge to edge, wrapping on narrow
            screens); the range filters sit on a second line at the left. */}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 w-full min-w-0">
          <FilterDropdown
            label="Period"
            width={160}
            menuWidth={180}
            isMulti={false}
            selected={royaltyDateFilter}
            options={RoyaltyDateOptionList}
            onSelect={onRoyaltyDateChange}
          />
          <FilterDropdown
            label="Recouped"
            width={180}
            menuWidth={200}
            isMulti={false}
            selected={recoupedFilter}
            options={RecoupedOptions}
            onSelect={onRecoupedChange}
          />
          <FilterDropdown
            label="Status"
            width={260}
            menuWidth={240}
            isMulti={true}
            selected={statusFilter}
            options={DealStatusOptions}
            onSelect={onStatusChange}
          />
          <FilterDropdown
            label="Previous Status"
            width={260}
            menuWidth={240}
            isMulti={true}
            selected={previousStatusFilter}
            options={DealStatusOptions}
            onSelect={onPreviousStatusChange}
          />
          <SearchUserComponent className="" selectedUser={selectedUser} setSelectedUser={setSelectedUser} />
          <KButton
            text="Apply Filters"
            background="black"
            textColor="white"
            onClick={() => onApplyFilter()}
            width="120px"
            height="36px"
          />
        </div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <RangeFilter
            label="Advance Size"
            minPlaceholder="Min $"
            maxPlaceholder="Max $"
            min={advanceMin}
            max={advanceMax}
            onChange={onAdvanceSizeChange}
          />
          <RangeFilter
            label="Target Recoup"
            minPlaceholder="Min mo"
            maxPlaceholder="Max mo"
            min={targetMin}
            max={targetMax}
            onChange={onTargetRecoupChange}
          />
        </div>
      </div>
      <div style={{ overflowX: "auto", opacity: loading ? 0.5 : 1, transition: "opacity 0.15s" }}>
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, ...stickyFirstColumn, zIndex: 2 }}>Artist</th>
              <th style={thStyle}>Total Advance</th>
              <th style={thStyle}>Recoupment</th>
              <th style={thStyle}>Outstanding</th>
              <th style={thStyle} title="Gross collections (all platforms) of the latest fully reported month">
                Latest Complete Revenue
              </th>
              <th style={thStyle}>Artist Rec. %</th>
              <th style={thStyle}>Company Rec. %</th>
              <th style={thStyle}>Closing In</th>
              <th style={thStyle}>Company Rec.</th>
              <th style={thStyle}>Target</th>
              <th style={thStyle}>Rec. Delta</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Previous Status</th>
              <th style={thStyle}>MOIC to Date</th>
              <th style={thStyle}>Total User Advance Count</th>
              <th style={thStyle}>First Advance Deployment Date</th>
              <th
                style={{ ...thStyle, cursor: "pointer", userSelect: "none" }}
                title="Sort by latest advance deployment date"
                onClick={onToggleLatestAdvanceSort}
              >
                {`Latest Advance Deployment Date ${
                  latestAdvanceSort === "asc" ? "▲" : latestAdvanceSort === "desc" ? "▼" : ""
                }`}
              </th>
              <th
                style={{ ...thStyle, cursor: "pointer", userSelect: "none" }}
                title="Sort by contract end date"
                onClick={onToggleContractEndSort}
              >
                {`Contract End ${contractEndSort === "asc" ? "▲" : contractEndSort === "desc" ? "▼" : ""}`}
              </th>
              <th style={thStyle}>Exclude</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.userId}
                className={row.excluded ? "[&>td:not(:first-child)]:opacity-[0.45]" : ""}
                style={row.status === "timing_review" ? { background: TIMING_REVIEW_ROW_BG } : undefined}
              >
                <td
                  style={{
                    ...tdStyle,
                    ...stickyFirstColumn,
                    ...(row.status === "timing_review" ? { background: TIMING_REVIEW_ROW_BG } : {}),
                    minWidth: 180
                  }}
                >
                  <div style={{ opacity: row.excluded ? 0.45 : 1 }}>
                    <ArtistCell name={row.artistName} image={row.artistImage} onClick={() => onSelectDeal(row)} />
                  </div>
                </td>
                <td style={tdStyle}>{usd0(row.advance)}</td>
                <td style={tdStyle}>{usd0(row.advance - row.outstanding)}</td>
                <td style={tdStyle}>{usd0(row.outstanding)}</td>
                <td
                  style={tdStyle}
                  title={
                    row.latestRevenue !== null && row.latestRevenue !== undefined
                      ? `Latest Complete Revenue\n${monthYear(latestCompleteMonth)}\n${usd0(
                          row.latestRevenue
                        )}\nAll platforms`
                      : undefined
                  }
                >
                  <div className="flex items-center gap-2">
                    {row.latestRevenue !== null && row.latestRevenue !== undefined ? (
                      usd0(row.latestRevenue)
                    ) : (
                      <span style={{ color: TEXT_MUTED }}>—</span>
                    )}
                    <ReportingStatusIcon row={row} />
                  </div>
                </td>
                <td style={{ ...tdStyle, minWidth: 130 }}>
                  <RecoupPctBar pct={row.artistRecoupPct} complete={row.artistRecoupPct >= 100} />
                </td>
                <td style={{ ...tdStyle, minWidth: 130 }}>
                  <RecoupPctBar pct={row.companyRecoupPct} complete={row.recouped} />
                </td>
                <td style={tdStyle}>{row.status !== "new" && row.closingIn ? `${row.closingIn} mo` : dash}</td>
                <td style={tdStyle}>{row.status === "new" ? dash : <CompanyRecoupCell row={row} />}</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{monthsText(row.targetRecoupMonths)}</td>
                <td style={tdStyle}>{row.status === "new" ? dash : <RecoupTimingDeltaCell row={row} />}</td>
                <td style={tdStyle}>
                  <StatusBadge status={row.status} />
                </td>
                <td style={{ ...tdStyle, color: TEXT_MUTED }}>
                  {row.previousStatus ? STATUS_STYLE[row.previousStatus]?.label ?? row.previousStatus : "N/A"}
                </td>
                <td style={tdStyle}>
                  {row.economicMoic === null || row.economicMoic === undefined
                    ? "N/A"
                    : `${row.economicMoic.toFixed(2)}x`}
                </td>
                <td style={tdStyle}>{row.advanceCount}</td>
                <td style={tdStyle}>{monthAbbrYear(row.firstAdvanceDate)}</td>
                <td style={tdStyle}>{monthAbbrYear(row.latestAdvanceDate)}</td>
                <td style={tdStyle}>{monthYear(row.contractEnd)}</td>
                <td style={{ ...tdStyle, textAlign: "center" }}>
                  <ExcludeToggleCell
                    userId={row.userId}
                    excluded={row.excluded}
                    isAdvanceDeal={true}
                    onToggle={onToggleExclude}
                  />
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td style={{ ...tdStyle, textAlign: "center", color: TEXT_MUTED }} colSpan={19}>
                  No advance deals
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination hidden={pageCount <= 1} currentPage={page} pageCount={pageCount} onClickPage={onPageChange} />
    </div>
  )
}

const renderPlatformStreamRow = (stream: number, platform: string) => {
  return (
    <div className="flex gap-1">
      <KSpan fontSize={13} text={`${platform}: `} />
      <KSpan fontSize={13} color={TEXT_PRIMARY} text={formatNumber(stream)} />
    </div>
  )
}

const renderPlatformRoyaltyRow = (royalty: number, platform: string) => {
  return (
    <div className="flex gap-1">
      <KSpan fontSize={13} text={`${platform}: `} />
      <KSpan
        fontSize={13}
        color={TEXT_PRIMARY}
        text={`$${royalty.toLocaleString("en-EN", { maximumFractionDigits: 2 })}`}
      />
    </div>
  )
}

const renderDistributionStreamBlock = (summaryMetric: DistributionSummaryMetrics) => {
  return (
    <td style={tdStyle}>
      <div className="flex flex-col gap-1">
        {renderPlatformStreamRow(summaryMetric.streamSpotify, "Spotify")}
        {renderPlatformStreamRow(summaryMetric.streamApple, "Apple")}
        {renderPlatformStreamRow(summaryMetric.streamYoutube, "Youtube")}
      </div>
    </td>
  )
}

const renderDistributionRoyaltyBlock = (summaryMetric: DistributionSummaryMetrics) => {
  return (
    <td style={tdStyle}>
      <div className="flex flex-col gap-1">
        {renderPlatformRoyaltyRow(summaryMetric.royaltySpotify, "Spotify")}
        {renderPlatformRoyaltyRow(summaryMetric.royaltyApple, "Apple")}
        {renderPlatformRoyaltyRow(summaryMetric.royaltyYoutube, "Youtube")}
      </div>
    </td>
  )
}

const renderDistributionRow = (
  row: DistributionDealRow,
  onToggleExclude: (userId: number, excluded: boolean, isAdvanceDeal: boolean) => void
) => {
  const kahunaLmRevenue = row.lm.totalAmount - row.lm.totalNet
  const kahunaLtmRevenue = row.ltm.totalAmount - row.ltm.totalNet
  const kahunaAllRevenue = row.all.totalAmount - row.all.totalNet

  return (
    <tr key={row.userId} style={{ opacity: row.excluded ? 0.45 : 1 }}>
      <td style={{ ...tdStyle, minWidth: 180 }}>
        <ArtistCell name={`${row.user?.firstName} ${row.user?.lastName || ""}`} image={row.user?.photo} />
      </td>
      <td style={tdStyle}>{monthYear(row.user?.dateJoined)}</td>
      <td style={tdStyle}>{row.userExpense?.distributionFee}</td>

      <td style={tdStyle}>{formatNumber(row.all.totalStream)}</td>
      <td style={tdStyle}>{`$${row.all.totalAmount.toLocaleString("en-EN", { maximumFractionDigits: 2 })}`}</td>
      <td style={tdStyle}>{`$${row.all.totalNet.toLocaleString("en-EN", { maximumFractionDigits: 2 })}`}</td>
      <td style={tdStyle}>{`$${kahunaAllRevenue.toLocaleString("en-EN", { maximumFractionDigits: 2 })}`}</td>
      {renderDistributionStreamBlock(row.all)}
      {renderDistributionRoyaltyBlock(row.all)}

      <td style={tdStyle}>{formatNumber(row.ltm.totalStream)}</td>
      <td style={tdStyle}>{`$${row.ltm.totalAmount.toLocaleString("en-EN", { maximumFractionDigits: 2 })}`}</td>
      <td style={tdStyle}>{`$${row.ltm.totalNet.toLocaleString("en-EN", { maximumFractionDigits: 2 })}`}</td>
      <td style={tdStyle}>{`$${kahunaLtmRevenue.toLocaleString("en-EN", { maximumFractionDigits: 2 })}`}</td>
      {renderDistributionStreamBlock(row.ltm)}
      {renderDistributionRoyaltyBlock(row.ltm)}

      <td style={tdStyle}>{formatNumber(row.lm.totalStream)}</td>
      <td style={tdStyle}>{`$${row.lm.totalAmount.toLocaleString("en-EN", { maximumFractionDigits: 2 })}`}</td>
      <td style={tdStyle}>{`$${row.lm.totalNet.toLocaleString("en-EN", { maximumFractionDigits: 2 })}`}</td>
      <td style={tdStyle}>{`$${kahunaLmRevenue.toLocaleString("en-EN", { maximumFractionDigits: 2 })}`}</td>
      {renderDistributionStreamBlock(row.lm)}
      {renderDistributionRoyaltyBlock(row.lm)}

      <td style={{ ...tdStyle, textAlign: "center" }}>
        <ExcludeToggleCell
          userId={row.userId}
          excluded={row.excluded}
          isAdvanceDeal={false}
          onToggle={onToggleExclude}
        />
      </td>
    </tr>
  )
}

const DistributionDealsTable: FC<{
  rows: DistributionDealRow[]
  count: number
  page: number
  distroFee: KSelectOption | undefined
  accountType: KSelectOption | undefined
  loading: boolean
  selectedUser: KSelectOption | undefined
  dateValues: DateRangeType
  setSelectedUser: (selectedUser: KSelectOption | undefined) => void
  onAccountTypeChange: (option: KSelectOption | KSelectOption[] | undefined) => void
  onApplyFilter: () => void
  onDistroFeeChange: (option: KSelectOption | KSelectOption[] | undefined) => void
  setDateValues: (dateValues: DateRangeType) => void
  onPageChange: (page: number) => void
  onToggleExclude: (userId: number, excluded: boolean, isAdvanceDeal: boolean) => void
}> = ({
  rows,
  count,
  page,
  selectedUser,
  distroFee,
  accountType,
  dateValues,
  loading,
  setDateValues,
  setSelectedUser,
  onAccountTypeChange,
  onApplyFilter,
  onDistroFeeChange,
  onPageChange,
  onToggleExclude
}) => {
  const pageCount = Math.max(1, Math.ceil(count / PAGE_SIZE))
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 min-w-0">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <KTitleSpan text="Distribution Deals" fontSize={16} fontWeight={700} />
        </div>
        <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-3 min-w-0">
          <div className="flex items-center">
            {
              //@ts-ignore
              dateValues?.[0] && <KSpan text={`${toLocaleDate(dateValues[0])} - ${toLocaleDate(dateValues[1])}`} />
            }
          </div>
          <KSelectRange
            key={`date_filter`}
            value={dateValues}
            onChange={(event) => setDateValues(event)}
            height="36px"
            width="auto"
            backgroundColor="#F7F7F7"
            hoverBackgroundColor="#F3F3F3"
            boxShadow="none"
            align={"center"}
          />
          <FilterDropdown
            label="Account Type"
            isMulti={false}
            width={120}
            menuWidth={160}
            selected={accountType}
            options={AccountTypeOptions}
            onSelect={onAccountTypeChange}
          />
          <FilterDropdown
            label="Distribution Fee"
            isMulti={false}
            width={120}
            menuWidth={160}
            selected={distroFee}
            options={DistributionFeeOptions}
            onSelect={onDistroFeeChange}
          />
          <SearchUserComponent className="" selectedUser={selectedUser} setSelectedUser={setSelectedUser} />
          <KButton
            text="Apply Filters"
            background="black"
            textColor="white"
            onClick={() => onApplyFilter()}
            width="120px"
            height="36px"
          />
        </div>
      </div>
      <div style={{ overflowX: "auto", opacity: loading ? 0.5 : 1, transition: "opacity 0.15s" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={thStyle}>Artist Name</th>
              <th style={thStyle}>Date Joined</th>
              <th style={thStyle}>Distribution Fee</th>

              <th style={thStyle}>All-Time Streams</th>
              <th style={thStyle}>All-Time Gross Rev.</th>
              <th style={thStyle}>All-Time Artist Rev.</th>
              <th style={thStyle}>All-Time Kahuna Rev.</th>
              <th style={thStyle}>All-Time Platform Streams</th>
              <th style={thStyle}>All-Time Platform Rev.</th>

              <th style={thStyle}>LTM Streams</th>
              <th style={thStyle}>LTM Gross Rev.</th>
              <th style={thStyle}>LTM Artist Rev.</th>
              <th style={thStyle}>LTM Kahuna Rev.</th>
              <th style={thStyle}>LTM Platform Streams</th>
              <th style={thStyle}>LTM Platform Rev.</th>

              <th style={thStyle}>LM Streams</th>
              <th style={thStyle}>LM Gross Rev.</th>
              <th style={thStyle}>LM Artist Rev.</th>
              <th style={thStyle}>LM Kahuna Rev.</th>
              <th style={thStyle}>LM Platform Streams</th>
              <th style={thStyle}>LM Platform Rev.</th>

              <th style={thStyle}>Exclude</th>
            </tr>
          </thead>
          <tbody>
            {rows
              .sort((a, b) => (a.all.totalAmount < b.all.totalAmount ? 1 : -1))
              .map((row) => renderDistributionRow(row, onToggleExclude))}
            {rows.length === 0 && (
              <tr>
                <td style={{ ...tdStyle, textAlign: "center", color: TEXT_MUTED }} colSpan={11}>
                  No distribution deals
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination hidden={pageCount <= 1} currentPage={page} pageCount={pageCount} onClickPage={onPageChange} />
    </div>
  )
}

const DEALS_PATH = "/admin/finance-reporting"

// ---- main -------------------------------------------------------------------

const AdminPortfolioOverview: FC<AdminPortfolioOverviewProps> = ({ setOpenSidebar, openSidebar }) => {
  // Full-page spinner only on the very first summary load; later fetches keep
  // the page mounted and dim the affected table instead.
  const [initialLoading, setInitialLoading] = useState(true)
  const [summaryKpi, setSummaryKpi] = useState<AdvancePortfolioSummaryKpi | null>(null)

  // Heavy, engine-based analytics — loaded on demand ("Click to calculate"),
  // mirroring the Performance tab so the page renders instantly on open.
  const [portfolioKpi, setPortfolioKpi] = useState<AdvancePortfolioKpi | null>(null)
  const [advancePortfolioTable, setAdvancePortfolioTable] = useState<AdvancePortfolioTable | null>(null)
  const [distributionPortfolioTable, setDistributionPortfolioTable] = useState<DistributionPortfolioTable | null>(null)
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false)
  const [distributionEnabled, setDistributionEnabled] = useState(false)
  const [analyticsLoading, setAnalyticsLoading] = useState(false)
  const [advanceLoading, setAdvanceLoading] = useState(false)
  const [distributionLoading, setDistributionLoading] = useState(false)
  const [showExcluded, setShowExcluded] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const [selectedDeal, setSelectedDeal] = useState<AdvanceDealRow>()
  const [dealNotFound, setDealNotFound] = useState(false)
  const wasOnDeal = useRef(false)
  const navigate = useNavigate()
  // The deal detail page lives at /admin/finance-reporting/<deal-id> (the deal id is the artist's user id).
  const dealId = useParams()["*"]
  const [dateValues, setDateValues] = useState<DateRangeType>([null, null])

  // ---- per-table search / filter / page state (server-side) ----------------
  const [summaryPage, setSummaryPage] = useState(1)

  const [advStatus, setAdvStatus] = useState<KSelectOption[]>([])
  const [advRecouped, setAdvRecouped] = useState<KSelectOption | undefined>()
  const [advPreviousStatus, setAdvPreviousStatus] = useState<KSelectOption[]>([])
  const [advMin, setAdvMin] = useState("")
  const [advMax, setAdvMax] = useState("")
  const [targetMin, setTargetMin] = useState("")
  const [targetMax, setTargetMax] = useState("")
  const [royaltyDateFilter, setRoyaltyDateFilter] = useState<KSelectOption | undefined>(RoyaltyDateOptionList[1])
  // Default sort: newest latest-advance first. Only one of the two sorts is active at a time.
  const [contractEndSort, setContractEndSort] = useState<"asc" | "desc" | undefined>()
  const [latestAdvanceSort, setLatestAdvanceSort] = useState<"asc" | "desc" | undefined>("desc")
  const [advPage, setAdvPage] = useState(1)
  const advanceTableRef = useRef<HTMLDivElement>(null)

  const [distroFee, setDistroFee] = useState<KSelectOption | undefined>()
  const [accountType, setAccountType] = useState<KSelectOption | undefined>()
  const [distroPage, setDistroPage] = useState(1)
  const [selectedUser, setSelectedUser] = useState<KSelectOption | undefined>()

  // The export is deliberately unfiltered: the workbook is the whole portfolio, and the
  // reader filters it in Excel. Only Show Excluded carries over, since that decides which
  // deals belong in the portfolio at all.
  const exportPortfolio = () => {
    setExportLoading(true)

    handleAuthorizedRequest(
      () =>
        users(`/user_advance_portfolio_export/?show_excluded=${showExcluded}`, "GET").then(async (response) => {
          // Keys are the workbook's column headers, so this payload is intentionally not
          // camel-cased on the way in.
          await downloadPortfolioExport(response.data as PortfolioExportPayload)
          setExportLoading(false)
          toast.success("Export ready", successFilledToastOptions)
        }),
      "Failed to build the portfolio export."
    ).catch(() => {
      setExportLoading(false)
      toast.error("Failed to build the portfolio export.", errorFilledToastOptions)
    })
  }

  const fetchSummaryKpi = () => {
    const query = `?show_excluded=${showExcluded}`

    users(`/user_advance_portfolio_summary_kpi/${query}`, "GET")
      .then((response) => {
        setSummaryKpi(mapToCamelCase<AdvancePortfolioSummaryKpi>(response.data))
        setInitialLoading(false)
      })
      .catch(() => {})
  }

  const fetchAdvancePortfolioKpi = () => {
    setAnalyticsLoading(true)
    const query = `?show_excluded=${showExcluded}`

    users(`/user_advance_portfolio_kpi/${query}`, "GET")
      .then((response) => {
        setPortfolioKpi(mapToCamelCase<AdvancePortfolioKpi>(response.data))
      })
      .catch(() => setAnalyticsLoading(false))
  }

  const fetchAdvancePortfolioTable = (offset: number, statusOptions: KSelectOption[] = advStatus) => {
    const userIdParam = selectedUser ? `&user_id=${selectedUser.value}` : ""
    const dealStatusParam =
      statusOptions.length > 0 ? `&deal_status=${statusOptions.map((option) => option.value2).join(",")}` : ""
    const recoupedParam = advRecouped ? `&recouped=${advRecouped.value2}` : ""
    const previousStatusParam =
      advPreviousStatus.length > 0 ? `&previous_status=${advPreviousStatus.map((o) => o.value2).join(",")}` : ""
    const royaltyDateParam = royaltyDateFilter ? `&royalty_date=${royaltyDateFilter.value2}` : ""
    const advanceSizeParam =
      advMin !== "" || advMax !== ""
        ? `&total_advance=${advMin === "" ? 0 : advMin},${advMax === "" ? ADVANCE_SIZE_OPEN_MAX : advMax}`
        : ""
    const targetRecoupParam =
      targetMin !== "" || targetMax !== ""
        ? `&target_recoup_months=${targetMin === "" ? 0 : targetMin},${
            targetMax === "" ? TARGET_RECOUP_OPEN_MAX : targetMax
          }`
        : ""
    const contractEndSortParam = contractEndSort ? `&contract_end_sort=${contractEndSort}` : ""
    const latestAdvanceSortParam = latestAdvanceSort ? `&latest_advance_sort=${latestAdvanceSort}` : ""

    const query = `?show_excluded=${showExcluded}${userIdParam}&offset=${offset}&limit=${PAGE_SIZE}${dealStatusParam}${recoupedParam}${previousStatusParam}${advanceSizeParam}${targetRecoupParam}${royaltyDateParam}${contractEndSortParam}${latestAdvanceSortParam}`

    setAdvanceLoading(true)
    users(`/user_advance_portfolio_table/${query}`, "GET")
      .then((response) => {
        setAdvancePortfolioTable(mapToCamelCase<AdvancePortfolioTable>(response.data))
        setAdvanceLoading(false)
      })
      .catch(() => setAdvanceLoading(false))
  }

  const fetchDistributionPortfolioTable = (offset: number) => {
    const userIdParam = selectedUser ? `&user_id=${selectedUser.value}` : ""
    const distroFeeParam = distroFee ? `&distribution_fee=${distroFee.value2}` : ""
    const accountTypeParam = accountType ? `&account_type=${accountType.value2}` : ""
    const dateParam =
      //@ts-ignore
      dateValues && dateValues?.[0] && dateValues?.[1]
        ? //@ts-ignore
          `&date=${dateToYYYYMM(dateValues[0])},${dateToYYYYMM(dateValues[1])}`
        : ""
    const query = `?show_excluded=${showExcluded}${userIdParam}${distroFeeParam}${accountTypeParam}${dateParam}&offset=${offset}&limit=${PAGE_SIZE}`

    setDistributionLoading(true)
    users(`/user_distribution_portfolio_table/${query}`, "GET")
      .then((response) => {
        setDistributionPortfolioTable(mapToCamelCase<DistributionPortfolioTable>(response.data))
        setDistributionLoading(false)
      })
      .catch(() => setDistributionLoading(false))
  }

  useEffect(() => {
    fetchSummaryKpi()
  }, [showExcluded])

  // Full analytics — fetched once enabled, then on any table param change.
  useEffect(() => {
    if (!analyticsEnabled) return
    fetchAdvancePortfolioKpi()
  }, [analyticsEnabled, showExcluded])

  useEffect(() => {
    fetchAdvancePortfolioTable((advPage - 1) * PAGE_SIZE)
  }, [showExcluded, advPage, contractEndSort, latestAdvanceSort])

  // Keeps the open deal in sync with the URL, so the browser's back/forward buttons and direct
  // links work: a row already on screen is reused, otherwise the deal is fetched on its own.
  useEffect(() => {
    setDealNotFound(false)
    if (!dealId) {
      setSelectedDeal(undefined)
      // A reload on a deal skips the first look at the list, so whatever it needs and has not
      // arrived (or failed while the page was still starting up) is requested again on return.
      if (wasOnDeal.current) {
        wasOnDeal.current = false
        if (initialLoading) fetchSummaryKpi()
        if (!advancePortfolioTable && !advanceLoading) fetchAdvancePortfolioTable((advPage - 1) * PAGE_SIZE)
      }
      return
    }
    wasOnDeal.current = true
    if (selectedDeal && String(selectedDeal.userId) === dealId) return

    const loaded = advancePortfolioTable?.advanceDeals.find((row) => String(row.userId) === dealId)
    if (loaded) {
      setSelectedDeal(loaded)
      return
    }

    let cancelled = false
    const royaltyDateParam = royaltyDateFilter ? `&royalty_date=${royaltyDateFilter.value2}` : ""
    users(
      `/user_advance_portfolio_table/?show_excluded=true&user_id=${encodeURIComponent(
        dealId
      )}&offset=0&limit=1${royaltyDateParam}`,
      "GET"
    )
      .then((response) => {
        if (cancelled) return
        const deal = mapToCamelCase<AdvancePortfolioTable>(response.data).advanceDeals?.[0]
        if (deal) setSelectedDeal(deal)
        else setDealNotFound(true)
      })
      .catch(() => !cancelled && setDealNotFound(true))
    return () => {
      cancelled = true
    }
  }, [dealId])

  // Header click cycles: ascending (nearest end first) → descending → default order.
  // The two sorts are mutually exclusive: switching to one clears the other.
  const onToggleContractEndSort = () => {
    setContractEndSort((prev) => (prev === "asc" ? "desc" : prev === "desc" ? undefined : "asc"))
    setLatestAdvanceSort(undefined)
    setAdvPage(1)
  }

  // First click sorts newest first.
  const onToggleLatestAdvanceSort = () => {
    setLatestAdvanceSort((prev) => (prev === "desc" ? "asc" : prev === "asc" ? undefined : "desc"))
    setContractEndSort(undefined)
    setAdvPage(1)
  }

  useEffect(() => {
    if (!distributionEnabled) return
    fetchDistributionPortfolioTable((distroPage - 1) * PAGE_SIZE)
  }, [showExcluded, distroPage])

  // Clicking a Portfolio Status bar filters the table to that status; clicking the only
  // active one again clears the filter.
  const onSelectPortfolioStatus = (status: string) => {
    const option = DealStatusOptions.find((o) => o.value2 === status)
    if (!option) return
    const alreadyOnly = advStatus.length === 1 && advStatus[0].value2 === status
    const next = alreadyOnly ? [] : [option]
    setAdvStatus(next)
    if (advPage === 1) {
      fetchAdvancePortfolioTable(0, next)
    } else {
      // the advPage effect refetches with the new status
      setAdvPage(1)
    }
    advanceTableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  const onApplyAdvanceFilter = () => {
    fetchAdvancePortfolioTable((summaryPage - 1) * PAGE_SIZE)
  }

  const onApplyDistributionFilter = () => {
    setDistributionEnabled(true)
    fetchDistributionPortfolioTable((summaryPage - 1) * PAGE_SIZE)
  }

  const onToggleShowExcluded = () => {
    setShowExcluded((prev) => !prev)
    setSummaryPage(1)
    setAdvPage(1)
    setDistroPage(1)
  }

  const onToggleExclude = (userId: number, currentlyExcluded: boolean, isAdvanceDeal: boolean) => {
    users("/update_exclude_portfolio/", "PUT", {
      user_id: userId,
      exclude_from_portfolio: !currentlyExcluded
    }).then((resp) => {
      if (!resp.data.success) {
        toast.error("Failed to update portfolio exclusion status", errorFilledToastOptions)
        return
      }
      toast.success("Portfolio exclusion status updated", successFilledToastOptions)
      if (isAdvanceDeal) {
        setAdvancePortfolioTable((prev) => {
          if (!prev) return prev
          return {
            ...prev,
            advanceDeals: prev.advanceDeals.map((deal) =>
              deal.userId === userId ? { ...deal, excluded: !currentlyExcluded } : deal
            )
          }
        })
      } else {
        setDistributionPortfolioTable((prev) => {
          if (!prev) return prev
          return {
            ...prev,
            distributionDeals: prev.distributionDeals.map((deal) =>
              deal.userId === userId ? { ...deal, excluded: !currentlyExcluded } : deal
            )
          }
        })
      }
    })
  }

  // Search / filter changes reset their table to the first page.
  const onAdvFilterChange =
    (setter: (option: KSelectOption | KSelectOption[] | undefined) => void) =>
    (option: KSelectOption | KSelectOption[] | undefined) => {
      setter(option)
      setAdvPage(1)
    }

  const onDistroFilterChange =
    (setter: (option: KSelectOption | KSelectOption[] | undefined) => void) =>
    (option: KSelectOption | KSelectOption[] | undefined) => {
      setter(option)
      setDistroPage(1)
    }

  if (dealId && !selectedDeal) {
    return dealNotFound ? (
      <div className="p-8 flex flex-col items-center gap-4 min-h-[400px] justify-center">
        <KSpan text="This deal could not be found." fontSize={14} lineHeight="20px" color="#111" />
        <KButton text="← Back" background="transparent" shadowDisabled onClick={() => navigate(DEALS_PATH)} />
      </div>
    ) : (
      <div className="p-8 flex justify-center items-center min-h-[400px]">
        <Loading />
      </div>
    )
  }

  if (selectedDeal && dealId) {
    return (
      <AdvanceDealDetailPage
        selectedDeal={selectedDeal}
        reportingPeriod={royaltyDateFilter?.value2 === "reporting_period"}
        onBack={() => navigate(DEALS_PATH)}
      />
    )
  }

  if (initialLoading) {
    return (
      <div className="p-8 flex justify-center items-center min-h-[400px]">
        <Loading />
      </div>
    )
  }

  const calculated = portfolioKpi !== null

  return (
    // min-w-0 lets the flex children actually shrink, so the wide deal tables scroll inside
    // their own overflow-x containers instead of stretching the page past the viewport.
    <div className="px-10 pt-5 pb-10 flex flex-col gap-6 min-w-0 max-w-full">
      <div className="flex justify-between items-center flex-wrap gap-3 min-w-0">
        <div className="flex items-center gap-2">
          <KTooltip
            backgroundColor="#000"
            padding="0px 4px"
            height="20px"
            arrowColor="#000"
            position="bottom"
            content={
              <div className="h-5 w-max flex items-center">
                <KSpan
                  text={openSidebar ? "Hide sidebar" : "Open sidebar"}
                  fontSize={12}
                  lineHeight="16px"
                  color="#FFF"
                />
              </div>
            }
          >
            <KButton
              dataTestId="portfolio-overview-sidebar-toggle-button"
              icon="/left-sidebar.svg"
              width="32px"
              height="32px"
              background="transparent"
              shadowDisabled={true}
              padding="6px"
              onClick={() => setOpenSidebar(!openSidebar)}
            />
          </KTooltip>
          <KTitleSpan text="Portfolio Overview" fontSize={24} fontWeight={700} color={TEXT_PRIMARY} />
        </div>
        <div className="flex flex-row items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showExcluded}
              onChange={onToggleShowExcluded}
              style={{ accentColor: "#111", cursor: "pointer" }}
            />
            <KSpan text="Show Excluded" fontSize={13} color={TEXT_MUTED} />
          </label>
          <KTooltip
            backgroundColor="#111"
            position="bottom"
            content={
              <div className="w-[240px] p-1">
                <KSpan
                  text="Deal, pool and monthly tapes as a single Excel workbook. The Run Info sheet records the report date and what this version leaves blank."
                  fontSize={12}
                  color="#FFF"
                />
              </div>
            }
          >
            <KButton
              text={exportLoading ? "Preparing..." : "Export Excel"}
              onClick={exportPortfolio}
              disabled={exportLoading}
              background="white"
              textColor={TEXT_PRIMARY}
            />
          </KTooltip>
          {!calculated && (
            <KButton
              text={analyticsLoading ? "Calculating..." : "Calculate full analytics"}
              onClick={() => setAnalyticsEnabled(true)}
              disabled={analyticsLoading}
              background={"black"}
              textColor={"white"}
            />
          )}
        </div>
      </div>

      {/* KPI cards — deployed/outstanding are instant; the rest are engine-based */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3 [&>*]:min-w-0">
        <KpiCard
          label="PORTFOLIO"
          value={`${summaryKpi?.totalArtists} artists`}
          subtext={`${summaryKpi?.totalAdvanceDeals} advances`}
        />
        <KpiCard
          label="TOTAL DEPLOYED"
          value={usd0(summaryKpi?.totalDeployed)}
          subtext={`Avg advance ${usd0(
            summaryKpi?.totalAdvanceDeals ? summaryKpi.totalDeployed / summaryKpi.totalAdvanceDeals : null
          )}`}
        />
        {calculated && portfolioKpi ? (
          <>
            <KpiCard
              label="OUTSTANDING"
              value={usd0(portfolioKpi?.outstanding)}
              subtext={`${pct(portfolioKpi?.outstandingPctOfDeployed)} of deployed`}
            />
            <KpiCard
              label="RECOUP TARGET"
              value={usd0(portfolioKpi.recoupTarget)}
              subtext={`Avg multiple ${multipleX(portfolioKpi.avgMultiple)}`}
            />
            <KpiCard
              label="TOTAL CASH TO KAHUNA"
              value={usd0((portfolioKpi.totalProfit ?? 0) + (portfolioKpi.totalRecouped ?? 0))}
              valueNote={{
                amount: usd0((portfolioKpi.recoupLast30D ?? 0) + (portfolioKpi.profitLast30D ?? 0)),
                text: "last month"
              }}
              subRows={[
                { label: "Recoupment", value: usd0(portfolioKpi.totalRecouped) },
                { label: "Profit", value: usd0(portfolioKpi.totalProfit) }
              ]}
            />
            {/* One IRR family across the page: the windowed figures are all annualized, and
                say so, so nothing reads as a different metric just because the header is shorter. */}
            <KpiCard label="12M IRR" value={pct(portfolioKpi.irr12M)} subtext="Annualized · first 12 months" />
            <KpiCard label="24M IRR" value={pct(portfolioKpi.irr24M)} subtext="Annualized · first 24 months" />
            <KpiCard label="36M IRR" value={pct(portfolioKpi.irr36M)} subtext="Annualized · first 36 months" />
            <KpiCard
              label="ECONOMIC MOIC"
              value={multipleX(portfolioKpi.economicMoic)}
              subtext="Collected to date ÷ deployed"
            />
            <KpiCard
              label="RECOUPED LAST MONTH"
              value={plural(portfolioKpi.recoupedDealsLast30D ?? 0, "advance")}
              subtext={`${plural(portfolioKpi.recoupedDealsArtistsLast30D ?? 0, "artist")} · ${compactUsd1(
                portfolioKpi.recoupedDealsAdvanceLast30D ?? 0
              )} advance`}
            />
          </>
        ) : (
          [
            "OUTSTANDING",
            "RECOUP TARGET",
            "TOTAL CASH TO KAHUNA",
            "12M IRR",
            "24M IRR",
            "36M IRR",
            "ECONOMIC MOIC",
            "RECOUPED LAST MONTH"
          ].map((label) => (
            <BlurredKpiCard
              key={label}
              label={label}
              isLoading={analyticsLoading}
              onClick={() => setAnalyticsEnabled(true)}
            />
          ))
        )}
      </div>

      {/* Charts + alert + full tables only after analytics are calculated */}
      {calculated && portfolioKpi && (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <KTitleSpan text="Monthly Cash Flow" fontSize={20} fontWeight={700} />
            <div className="mt-1">
              <KSpan
                text="Monthly collections, new advances, net cash, and the cumulative trend"
                fontSize={14}
                color={TEXT_MUTED}
              />
            </div>
            <div style={{ height: 460, marginTop: 16, position: "relative" }}>
              <MonthlyCashFlowChart portfolio={portfolioKpi} />
            </div>
          </div>
          {/* The two cards use identical fixed header and chart heights, so the titles and the plots
              line up exactly. */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 [&>*]:min-w-0">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col">
              <div className="flex items-center" style={{ height: CHART_CARD_HEADER_HEIGHT }}>
                <KTitleSpan text="Portfolio Status" fontSize={20} fontWeight={700} lineHeight="28px" />
              </div>
              <div style={{ height: CHART_CARD_PLOT_HEIGHT, marginTop: 16, position: "relative" }}>
                <PortfolioStatusChart
                  portfolio={portfolioKpi}
                  activeStatuses={advStatus.map((option) => option.value2 as string)}
                  onSelectStatus={onSelectPortfolioStatus}
                />
              </div>
              <div className="flex gap-3 mt-4">
                <TimingReviewMovementCard
                  label="ENTERED TIMING REVIEW"
                  movement={portfolioKpi.portfolioStatusChart?.timingReviewMovement?.entered}
                />
                <TimingReviewMovementCard
                  label="EXITED TIMING REVIEW"
                  movement={portfolioKpi.portfolioStatusChart?.timingReviewMovement?.exited}
                />
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col">
              <div className="flex justify-between items-center gap-3" style={{ height: CHART_CARD_HEADER_HEIGHT }}>
                <KTitleSpan text="Monthly Vintage Recovery" fontSize={20} fontWeight={700} lineHeight="28px" />
                <div
                  className="shrink-0 px-3 py-1 border border-gray-100 rounded-lg bg-gray-50 text-right"
                  title="Total principal recouped ÷ total deployed, across the whole portfolio"
                >
                  <div>
                    <KSpan
                      text="Portfolio Recoup"
                      fontSize={12}
                      fontWeight={500}
                      color={TEXT_MUTED}
                      lineHeight="16px"
                    />
                  </div>
                  <div className="text-center">
                    <KTitleSpan
                      text={pct(
                        portfolioKpi.vintageRecoveryChart?.totalDeployed
                          ? (portfolioKpi.vintageRecoveryChart.totalPrincipalRecouped /
                              portfolioKpi.vintageRecoveryChart.totalDeployed) *
                              100
                          : null
                      )}
                      fontSize={16}
                      fontWeight={700}
                      color={TEXT_PRIMARY}
                      lineHeight="22px"
                    />
                  </div>
                </div>
              </div>
              <div style={{ height: CHART_CARD_PLOT_HEIGHT, marginTop: 16, position: "relative" }}>
                <MonthlyVintageChart portfolio={portfolioKpi} />
                <VintageLegend />
              </div>
              <div className="flex gap-3 mt-4">
                <SplitSummaryCard
                  label="AVG RECOUP TIME"
                  columns={[
                    {
                      value: monthsOneDecimal(portfolioKpi.vintageRecoveryChart?.avgTargetRecoup),
                      caption: "Target Recoup"
                    },
                    {
                      value: monthsOneDecimal(portfolioKpi.vintageRecoveryChart?.avgProjectedRecoup),
                      caption: "Projected Recoup"
                    }
                  ]}
                />
                <SplitSummaryCard
                  label="COMPANY / ARTIST RECOUPED"
                  columns={[
                    {
                      value: plural(portfolioKpi.vintageRecoveryChart?.companyRecoupedArtists ?? 0, "artist"),
                      caption: `${usd0(portfolioKpi.vintageRecoveryChart?.companyRecoupedAdvance ?? 0)} advance`
                    },
                    {
                      value: plural(portfolioKpi.vintageRecoveryChart?.artistRecoupedArtists ?? 0, "artist"),
                      caption: `${usd0(portfolioKpi.vintageRecoveryChart?.artistRecoupedAdvance ?? 0)} advance`
                    }
                  ]}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {advancePortfolioTable && (
        <div ref={advanceTableRef}>
          <AdvanceDealsTable
            rows={advancePortfolioTable.advanceDeals}
            latestCompleteMonth={advancePortfolioTable.latestCompleteMonth}
            count={advancePortfolioTable.advanceDealsCount}
            page={advPage}
            statusFilter={advStatus}
            recoupedFilter={advRecouped}
            previousStatusFilter={advPreviousStatus}
            advanceMin={advMin}
            advanceMax={advMax}
            targetMin={targetMin}
            targetMax={targetMax}
            royaltyDateFilter={royaltyDateFilter}
            contractEndSort={contractEndSort}
            latestAdvanceSort={latestAdvanceSort}
            selectedUser={selectedUser}
            setSelectedUser={setSelectedUser}
            loading={advanceLoading}
            onApplyFilter={onApplyAdvanceFilter}
            //@ts-ignore
            onStatusChange={onAdvFilterChange(setAdvStatus)}
            //@ts-ignore
            onRecoupedChange={onAdvFilterChange(setAdvRecouped)}
            //@ts-ignore
            onPreviousStatusChange={onAdvFilterChange(setAdvPreviousStatus)}
            onTargetRecoupChange={(min, max) => {
              setTargetMin(min)
              setTargetMax(max)
              setAdvPage(1)
            }}
            onAdvanceSizeChange={(min, max) => {
              setAdvMin(min)
              setAdvMax(max)
              setAdvPage(1)
            }}
            //@ts-ignore
            onRoyaltyDateChange={onAdvFilterChange(setRoyaltyDateFilter)}
            onToggleContractEndSort={onToggleContractEndSort}
            onToggleLatestAdvanceSort={onToggleLatestAdvanceSort}
            onPageChange={setAdvPage}
            onToggleExclude={onToggleExclude}
            onSelectDeal={(deal) => {
              setSelectedDeal(deal)
              navigate(`${DEALS_PATH}/${deal.userId}`)
            }}
          />
        </div>
      )}
      {portfolioKpi?.riskConcentration && (
        <div className="mt-5">
          <RiskConcentrationCard risk={portfolioKpi.riskConcentration} />
        </div>
      )}
      {distributionPortfolioTable ? (
        <DistributionDealsTable
          rows={distributionPortfolioTable.distributionDeals}
          count={distributionPortfolioTable.distributionDealsCount}
          page={distroPage}
          selectedUser={selectedUser}
          dateValues={dateValues}
          onApplyFilter={onApplyDistributionFilter}
          setSelectedUser={setSelectedUser}
          distroFee={distroFee}
          accountType={accountType}
          loading={distributionLoading}
          //@ts-ignore
          onDistroFeeChange={onDistroFilterChange(setDistroFee)}
          //@ts-ignore
          onAccountTypeChange={onDistroFilterChange(setAccountType)}
          setDateValues={setDateValues}
          onPageChange={setDistroPage}
          onToggleExclude={onToggleExclude}
        />
      ) : (
        <div className="mt-4 flex justify-center">
          <KButton
            width="240px"
            background="black"
            textColor="white"
            text="Load Distribution Table"
            onClick={() => onApplyDistributionFilter()}
          />
        </div>
      )}
    </div>
  )
}

export default AdminPortfolioOverview
